import { Ingredient, Tool, Transformation, searchIngredients, searchTools, searchTransformations } from '../../api/index.js';
import { getDefaultImageUrl } from '../imageUtils.js';

/**
 * SearchDropdown component with a custom div-based results list.
 *
 * Renders in normal document flow — a plain block sibling right after the
 * input, not `absolute`/`fixed` — so opening it pushes whatever comes after
 * it (the create button, other fields below) down, the same way a native
 * `<select>` growing in place would. This is a deliberate third attempt
 * after two overlay-based approaches each solved one problem and reopened
 * another: `absolute` (nested in the popup) flows and scrolls correctly
 * but gets clipped by the popup's `overflow-y: auto` the moment it's
 * taller than the room left below the input; `fixed` (escaped to `<body>`)
 * never clips but needs its position computed and kept in sync by hand,
 * which iOS Safari's on-screen keyboard made unreliable (it pans the
 * visual viewport independently of the layout viewport
 * getBoundingClientRect() reports, in ways that don't fire regular scroll
 * events at a steady rate). Plain flow layout has neither failure mode —
 * nothing to clip against, nothing to track — at the cost of visibly
 * shifting the layout below it while open. See git history for both
 * overlay-based versions this replaced, if that trade-off ever needs
 * revisiting.
 */
export class SearchDropdown {
  private type: 'ingredient' | 'transformation' | 'tool';
  private input: HTMLInputElement;
  private dropdownContainer: HTMLElement;
  /** The ancestor shared by the input and the dropdown — used only to
   *  detect outside clicks. */
  private wrapper: HTMLElement;
  /** null for a suggestions-only dropdown (e.g. the node name field's
   *  ingredient suggestions) — nothing to create there, just pick or ignore. */
  private createButton: HTMLButtonElement | null;
  private results: (Ingredient | Transformation | Tool)[] = [];
  private searchTimeout: number | null = null;
  private onSelect: (item: Ingredient | Transformation | Tool) => void;
  private onCreateNew: ((query: string) => void) | null;
  /** Whether picking a result overwrites the input's text with its name —
   *  on for the ingredient/transformation tabs' own search box (the input
   *  *is* "the current selection"), off for a suggestions-only dropdown
   *  attached to an unrelated field (e.g. the node name field), where the
   *  typed text must stay untouched. */
  private updateInputOnSelect: boolean;
  private inputHandler: ((e: Event) => void) | null = null;
  private createButtonHandler: ((e: Event) => void) | null = null;
  private keydownHandler: ((e: KeyboardEvent) => void) | null = null;
  private outsideClickHandler: ((e: MouseEvent) => void) | null = null;

  constructor(
    type: 'ingredient' | 'transformation' | 'tool',
    input: HTMLInputElement,
    dropdownContainer: HTMLElement,
    createButton: HTMLButtonElement | null,
    onSelect: (item: Ingredient | Transformation | Tool) => void,
    onCreateNew?: (query: string) => void,
    updateInputOnSelect: boolean = true,
    /** Boundary for outside-click detection. Defaults to the dropdown's own
     *  parent element, which is right for a plain suggestions dropdown
     *  (input + dropdown, nothing else). Pass this explicitly when the
     *  wrapper also holds other interactive elements the dropdown shouldn't
     *  close on — e.g. renderSearchDropdown()'s create button, a sibling
     *  appended after the dropdown rather than inside it. */
    outsideClickBoundary?: HTMLElement
  ) {
    this.type = type;
    this.input = input;
    this.dropdownContainer = dropdownContainer;
    this.wrapper = outsideClickBoundary ?? dropdownContainer.parentElement ?? input.parentElement ?? input;
    this.createButton = createButton;
    this.onSelect = onSelect;
    this.onCreateNew = onCreateNew ?? null;
    this.updateInputOnSelect = updateInputOnSelect;
  }

  /**
   * Initialize event listeners
   */
  init(): void {
    // Input event with debouncing
    this.inputHandler = (e: Event) => {
      const query = (e.target as HTMLInputElement).value.trim();

      if (this.searchTimeout !== null) {
        window.clearTimeout(this.searchTimeout);
      }

      this.searchTimeout = window.setTimeout(() => {
        if (query.length > 0) {
          this.search(query);
        } else {
          this.hideResults();
        }
      }, 300);
    };

    // Create button event (suggestions-only dropdowns have neither)
    if (this.createButton && this.onCreateNew) {
      this.createButtonHandler = () => {
        this.onCreateNew!(this.input.value.trim());
        this.hideResults();
      };
      this.createButton.addEventListener('click', this.createButtonHandler);
    }

    // Escape key to clear
    this.keydownHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        this.hideResults();
      }
    };

    this.input.addEventListener('input', this.inputHandler);
    this.input.addEventListener('keydown', this.keydownHandler);

    // Click outside (of the shared wrapper) to close.
    this.outsideClickHandler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!this.wrapper.contains(target)) {
        this.hideResults();
      }
    };
    document.addEventListener('click', this.outsideClickHandler);
  }

  /**
   * Perform search with API call
   */
  private async search(query: string): Promise<void> {
    try {
      // Fetch more than the ~5 that fit in the dropdown's fixed visible
      // height (see displayResults()) — some queries genuinely match many
      // entities, and the rest are still reachable by scrolling rather
      // than being silently unfindable.
      if (this.type === 'ingredient') {
        this.results = await searchIngredients(query, 20);
      } else if (this.type === 'tool') {
        this.results = await searchTools(query, 20);
      } else {
        this.results = await searchTransformations(query, 20);
      }
      this.displayResults(query);
    } catch (error) {
      console.error(`Failed to search ${this.type}s:`, error);
      this.results = [];
      this.displayResults(query);
    }
  }

  /**
   * Display search results as clickable div rows, each with the entity's
   * default image (if it has one) and name — same layout as the home
   * page's ingredient filter dropdown (SearchSection).
   */
  private displayResults(query: string): void {
    // A suggestions-only dropdown (no create button) has nothing worth
    // showing once there are no matches — just close it, rather than
    // leaving an empty "no results" box open with no action to take.
    if (this.results.length === 0 && !this.createButton) {
      this.hideResults();
      return;
    }

    this.dropdownContainer.innerHTML = '';

    if (this.results.length === 0) {
      const noResults = document.createElement('div');
      noResults.className = 'px-4 py-2 text-sm text-[#888]';
      noResults.textContent = 'No s\'han trobat resultats';
      this.dropdownContainer.appendChild(noResults);
    } else {
      for (const item of this.results) {
        const row = document.createElement('div');
        // h-9: a fixed row height, not just content-driven padding — so
        // the dropdown's max-height (5 * this) reliably shows exactly
        // ~5 rows regardless of font rendering, with the rest reachable
        // by scrolling.
        row.className =
          'h-9 px-4 py-2 text-sm hover:bg-[var(--accent-alpha-13)] cursor-pointer transition-colors flex items-center gap-2';

        const imageUrl = getDefaultImageUrl(item.images);
        if (imageUrl) {
          const img = document.createElement('img');
          img.src = imageUrl;
          img.alt = item.name;
          img.className = 'rounded-full';
          img.style.cssText = 'width: 20px; height: 20px; object-fit: cover; flex-shrink: 0;';
          row.appendChild(img);
        }

        const nameSpan = document.createElement('span');
        nameSpan.textContent = item.name;
        row.appendChild(nameSpan);

        row.addEventListener('click', () => {
          this.onSelect(item);
          if (this.updateInputOnSelect) {
            this.input.value = item.name;
          }
          this.hideResults();
        });
        this.dropdownContainer.appendChild(row);
      }
    }

    // Append create button below search results, if this dropdown has one
    if (this.createButton) {
      this.createButton.textContent = `Crea "${query}"`;
      this.createButton.style.display = 'block';
      this.dropdownContainer.appendChild(this.createButton);
    }

    this.dropdownContainer.style.display = 'block';
    // In-flow layout means the results just pushed content below them
    // down rather than overlaying anything — nudge the popup's scroll (if
    // any) so the newly-revealed results are actually visible instead of
    // requiring a manual scroll to see what just appeared.
    this.dropdownContainer.scrollIntoView({ block: 'nearest' });
  }

  /**
   * Hide results dropdown and create button
   */
  private hideResults(): void {
    this.dropdownContainer.style.display = 'none';
    this.dropdownContainer.innerHTML = '';
    if (this.createButton) {
      this.createButton.style.display = 'none';
    }
  }

  /**
   * Cleanup event listeners and remove the dropdown element itself.
   */
  destroy(): void {
    if (this.inputHandler) {
      this.input.removeEventListener('input', this.inputHandler);
    }
    if (this.keydownHandler) {
      this.input.removeEventListener('keydown', this.keydownHandler);
    }
    if (this.createButtonHandler && this.createButton) {
      this.createButton.removeEventListener('click', this.createButtonHandler);
    }
    if (this.outsideClickHandler) {
      document.removeEventListener('click', this.outsideClickHandler);
    }
    if (this.searchTimeout !== null) {
      window.clearTimeout(this.searchTimeout);
    }
    this.hideResults();
    this.dropdownContainer.remove();
  }
}
